import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import SendIcon from "@mui/icons-material/Send";
import PhoneIcon from "@mui/icons-material/Phone";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import authAxios from "../../authAxios";

const CONCIERGE_SEEN_KEY = "concierge_intro_seen";
const CONCIERGE_CALL_KEY = "concierge_call_ref_id";

const slides = [
  {
    icon: <PhoneIcon style={{ fontSize: 48, color: "#111" }} />,
    textKey: "concierge_intro_slide1",
    defaultText: "We help you to call any restaurant in the world and make a reservation.",
  },
  {
    icon: <AttachMoneyIcon style={{ fontSize: 48, color: "#111" }} />,
    textKey: "concierge_intro_slide2",
    defaultText: "Your first 2 reservations are free.",
  },
];

const IntroModal = ({ onClose }) => {
  const { t } = useTranslation();
  const [slide, setSlide] = useState(0);
  const isLast = slide === slides.length - 1;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 99999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0,0,0,0.45)",
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.92, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 260, damping: 24 }}
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: "#fff",
          borderRadius: 20,
          padding: "32px 28px 24px",
          width: "min(360px, calc(100vw - 40px))",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0,
          boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
        }}
      >
        {/* Header */}
        <h2
          style={{
            margin: "0 0 24px",
            fontSize: 20,
            fontWeight: 700,
            color: "#111",
            textAlign: "center",
          }}
        >
          {t("concierge_intro_header", { defaultValue: "Introducing Concierge" })}
        </h2>

        {/* Slide content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={slide}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.22 }}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 16,
              minHeight: 120,
              width: "100%",
            }}
          >
            <div
              style={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                backgroundColor: "#efefef",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {slides[slide].icon}
            </div>
            <p
              style={{
                margin: 0,
                fontSize: 15,
                lineHeight: 1.6,
                color: "#444",
                textAlign: "center",
              }}
            >
              {t(slides[slide].textKey, { defaultValue: slides[slide].defaultText })}
            </p>
          </motion.div>
        </AnimatePresence>

        {/* Dots */}
        <div style={{ display: "flex", gap: 6, marginTop: 24 }}>
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setSlide(i)}
              style={{
                width: i === slide ? 20 : 8,
                height: 8,
                borderRadius: 4,
                border: "none",
                backgroundColor: i === slide ? "black" : "#d0d0d0",
                padding: 0,
                cursor: "pointer",
                transition: "width 0.25s, background-color 0.25s",
              }}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>

        {/* Action button */}
        <button
          onClick={isLast ? onClose : () => setSlide((s) => s + 1)}
          style={{
            marginTop: 20,
            width: "100%",
            height: 44,
            borderRadius: 22,
            border: "none",
            backgroundColor: "#111",
            color: "#fff",
            fontSize: 15,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          {isLast
            ? t("concierge_intro_got_it", { defaultValue: "Got it" })
            : t("concierge_intro_next", { defaultValue: "Next" })}
        </button>
      </motion.div>
    </div>
  );
};

const renderWithBold = (text) => {
  const tokens = [];
  const regex = /\*\*(.+?)\*\*|\[([^\]]+)\]\((https?:\/\/[^)]+)\)|(https?:\/\/[^\s]+)/g;
  let last = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) tokens.push({ type: "text", value: text.slice(last, match.index) });
    if (match[1] !== undefined) tokens.push({ type: "bold", value: match[1] });
    else if (match[2] !== undefined)
      tokens.push({ type: "mdlink", label: match[2], url: match[3] });
    else tokens.push({ type: "url", value: match[0] });
    last = regex.lastIndex;
  }
  if (last < text.length) tokens.push({ type: "text", value: text.slice(last) });
  return tokens.map((token, i) => {
    if (token.type === "bold") return <strong key={i}>{token.value}</strong>;
    if (token.type === "mdlink")
      return (
        <a
          key={i}
          href={token.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "inherit", textDecoration: "underline" }}
        >
          {token.label}
        </a>
      );
    if (token.type === "url")
      return (
        <a
          key={i}
          href={token.value}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "inherit", textDecoration: "underline" }}
        >
          {token.value}
        </a>
      );
    return token.value;
  });
};

const TypingIndicator = () => (
  <div style={{ display: "flex", alignItems: "center", gap: "4px", padding: "10px 12px" }}>
    {[0, 1, 2].map((i) => (
      <motion.div
        key={i}
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: "#999",
        }}
        animate={{ y: [0, -6, 0] }}
        transition={{
          duration: 0.6,
          repeat: Infinity,
          delay: i * 0.15,
        }}
      />
    ))}
  </div>
);

export const ConciergeChatPanel = () => {
  const { t } = useTranslation();

  const [showIntro, setShowIntro] = useState(() => !localStorage.getItem(CONCIERGE_SEEN_KEY));

  const [messages, setMessages] = useState([
    { id: 1, role: "bot", text: t("concierge_greeting") },
    { id: 2, role: "bot", text: t("concierge_greeting_2") },
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [showChips, setShowChips] = useState(true);
  const [callInProgress, setCallInProgress] = useState(false);
  const messagesEndRef = useRef(null);
  const userStartedChatting = useRef(false);
  const hasDbHistory = useRef(false);
  const lastDbMessageCountRef = useRef(0);

  const loadHistory = ({ guardStarted = false } = {}) => {
    authAxios.get("/v1/concierge/messages").then(({ data }) => {
      if (guardStarted && userStartedChatting.current) return;
      if (data.data.length > 0) {
        hasDbHistory.current = true;
        setMessages(
          data.data.map((m) => ({
            id: m.id,
            role: m.role === "assistant" ? "bot" : "user",
            text: m.content,
          }))
        );
      }
    });
  };

  useEffect(() => {
    const storedRef = localStorage.getItem(CONCIERGE_CALL_KEY);
    if (storedRef) {
      lastDbMessageCountRef.current = Number(storedRef);
      setCallInProgress(true);
      setIsTyping(true);
      loadHistory();
    } else {
      loadHistory({ guardStarted: true });
    }
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  // Poll for call result while a background call is in progress
  useEffect(() => {
    if (!callInProgress) return;
    const interval = setInterval(async () => {
      try {
        const { data } = await authAxios.get("/v1/concierge/messages");
        const msgs = data.data;
        const callActive = data.callActive;
        const currentRef = lastDbMessageCountRef.current;
        console.log(
          "[concierge poll] ref:",
          currentRef,
          "callActive:",
          callActive,
          "messages:",
          msgs.map((m) => ({ id: m.id, role: m.role, preview: m.content?.slice(0, 40) }))
        );
        const newMsgs = msgs.filter((m) => m.id > currentRef);
        if (newMsgs.length > 0) {
          const maxId = Math.max(...msgs.map((m) => m.id));
          const hasNewAssistant = newMsgs.some((m) => m.role === "assistant");
          console.log(
            "[concierge poll] new message(s) detected — advancing ref from",
            currentRef,
            "to",
            maxId,
            "hasNewAssistant:",
            hasNewAssistant,
            "callActive:",
            callActive
          );
          lastDbMessageCountRef.current = maxId;
          setMessages(
            msgs.map((m) => ({
              id: m.id,
              role: m.role === "assistant" ? "bot" : "user",
              text: m.content,
            }))
          );
          if (!callActive && hasNewAssistant) {
            // Got an assistant response and no more background work — stop polling
            localStorage.removeItem(CONCIERGE_CALL_KEY);
            setCallInProgress(false);
            setIsTyping(false);
          } else {
            // Only user messages so far, or callActive still true — keep polling
            localStorage.setItem(CONCIERGE_CALL_KEY, String(maxId));
          }
        } else {
          console.log("[concierge poll] no new messages yet (ref:", currentRef, ")");
        }
      } catch (err) {
        console.error("[concierge] poll error:", err);
        // Transient error — keep polling, it will self-recover
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [callInProgress]);

  const handleCloseIntro = () => {
    localStorage.setItem(CONCIERGE_SEEN_KEY, "1");
    setShowIntro(false);
  };

  const sendMessage = async (text) => {
    if (!text.trim() || isTyping) return;
    const shouldResetGreeting = !userStartedChatting.current && !hasDbHistory.current;
    userStartedChatting.current = true;
    const userMsg = { id: Date.now(), role: "user", text: text.trim() };
    setMessages((prev) => (shouldResetGreeting ? [userMsg] : [...prev, userMsg]));
    setInputValue("");
    setShowChips(false);
    setIsTyping(true);

    try {
      const { data } = await authAxios.post("/v1/concierge/messages", {
        message: text.trim(),
        clientDatetime: new Date().toISOString(),
      });
      const { assistantMessage, callInProgress: callStarted } = data.data;
      if (assistantMessage) {
        setMessages((prev) => [
          ...prev,
          {
            id: assistantMessage.id,
            role: "bot",
            text: assistantMessage.content,
          },
        ]);
      }
      if (callStarted) {
        // Keep typing indicator on; polling will clear it when call result arrives
        const refId = assistantMessage ? assistantMessage.id : lastDbMessageCountRef.current;
        console.log(
          "[concierge] call started — setting poll ref to",
          refId,
          "(assistantMessage:",
          assistantMessage?.id,
          ")"
        );
        localStorage.setItem(CONCIERGE_CALL_KEY, String(refId));
        lastDbMessageCountRef.current = refId;
        setCallInProgress(true);
      } else {
        setIsTyping(false);
      }
    } catch (err) {
      setIsTyping(false);
      throw err;
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  return (
    <>
      {showIntro && <IntroModal onClose={handleCloseIntro} />}

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          padding: "12px 0 0",
        }}
      >
        {/* Chat card */}
        <div
          style={{
            flex: 1,
            margin: "0 12px",
            borderRadius: 16,
            backgroundColor: "#fff",
            boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            padding: "16px",
          }}
        >
          {messages.map((msg) => (
            <div
              key={msg.id}
              style={{
                display: "flex",
                justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
              }}
            >
              <div
                style={{
                  maxWidth: "75%",
                  padding: "10px 14px",
                  borderRadius: msg.role === "user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                  backgroundColor: msg.role === "user" ? "#222" : "#f3f3f3",
                  color: msg.role === "user" ? "#fff" : "#222",
                  fontSize: "14px",
                  lineHeight: "1.5",
                  whiteSpace: "pre-wrap",
                }}
              >
                {renderWithBold(msg.text)}
              </div>
            </div>
          ))}

          {/* Typing indicator */}
          <AnimatePresence>
            {isTyping && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                style={{ display: "flex", justifyContent: "flex-start" }}
              >
                <div
                  style={{
                    backgroundColor: "#f3f3f3",
                    borderRadius: "16px 16px 16px 4px",
                    display: "inline-block",
                  }}
                >
                  <TypingIndicator />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div ref={messagesEndRef} />
        </div>

        {/* Input bar — separate pill container below chat card */}
        <form
          onSubmit={handleSubmit}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "10px 16px",
            margin: "8px 12px 12px",
            borderRadius: 28,
            backgroundColor: "#fff",
            boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
            border: "1px solid #e8e8e8",
          }}
        >
          <button
            type="button"
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              border: "1px solid #e0e0e0",
              backgroundColor: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              flexShrink: 0,
            }}
            aria-label={t("concierge_menu", { defaultValue: "Menu" })}
          >
            <span style={{ fontSize: "16px", color: "#555" }}>≡</span>
          </button>

          <input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={t("concierge_input_placeholder", { defaultValue: "Ask me anything..." })}
            style={{
              flex: 1,
              height: 40,
              borderRadius: 20,
              border: "1px solid #e0e0e0",
              padding: "0 16px",
              fontSize: "14px",
              outline: "none",
              backgroundColor: "#fafafa",
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendMessage(inputValue);
              }
            }}
          />

          <button
            type="submit"
            disabled={!inputValue.trim()}
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              border: "none",
              backgroundColor: inputValue.trim() ? "#222" : "#e0e0e0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: inputValue.trim() ? "pointer" : "default",
              flexShrink: 0,
              transition: "background-color 0.2s",
            }}
            aria-label={t("concierge_send", { defaultValue: "Send" })}
          >
            <SendIcon style={{ fontSize: "16px", color: "#fff" }} />
          </button>
        </form>
      </div>
    </>
  );
};
